/**
 * @file 节点缩放控制点组件
 * 在选中节点时显示8个控制点，用于调整节点大小
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Core, NodeSingular, Position } from 'cytoscape';
import {
  ResizeHandle,
  getResizeHandlePositions,
  getResizeCursor,
  calculateResizedDimensions,
  getMinNodeSize,
  updateNodeSizeToModel,
  clearLabelBBoxCache,
} from '../controller/node-interaction';

interface ResizeHandlesProps {
  // cytoscape 实例
  cy: Core | null;
  // 选中的节点
  selectedNode: NodeSingular | null;
  // 缩放完成回调
  onResizeEnd?: (node: NodeSingular, width: number, height: number) => void;
  // 控制点大小
  handleSize?: number;
}

const HANDLE_SIZE = 8;
const HANDLE_COLOR = '#4a90d9';

const ResizeHandles: React.FC<ResizeHandlesProps> = ({
  cy,
  selectedNode,
  onResizeEnd,
  handleSize = HANDLE_SIZE,
}) => {
  const [handles, setHandles] = useState<Record<ResizeHandle, Position> | null>(
    null
  );
  const [activeHandle, setActiveHandle] = useState<ResizeHandle | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const startPosRef = useRef<Position | null>(null);
  const startNodePosRef = useRef<Position | null>(null); // 记录拖动开始时的节点位置
  const startNodeSizeRef = useRef<{ width: number; height: number } | null>(
    null
  ); // 记录开始时的节点尺寸
  const containerRef = useRef<HTMLDivElement>(null);

  // 缓存 selectedNode id
  const selectedNodeId = selectedNode?.id();

  // 更新控制点位置
  const updateHandlePositions = useCallback(() => {
    if (!selectedNode || !cy) {
      setHandles(null);
      return;
    }

    const positions = getResizeHandlePositions(selectedNode);
    setHandles(positions);
  }, [selectedNode, cy]);

  // 监听节点变化更新控制点位置
  useEffect(() => {
    updateHandlePositions();

    if (!selectedNodeId || !cy) return;

    // 监听节点位置/尺寸变化
    const handleNodeChange = () => {
      updateHandlePositions();
    };

    cy.on('position', `#${selectedNodeId}`, handleNodeChange);
    cy.on('style', `#${selectedNodeId}`, handleNodeChange);
    cy.on('pan zoom', handleNodeChange);

    return () => {
      cy.off('position', `#${selectedNodeId}`, handleNodeChange);
      cy.off('style', `#${selectedNodeId}`, handleNodeChange);
      cy.off('pan zoom', handleNodeChange);
    };
  }, [selectedNodeId, cy, updateHandlePositions]);

  // 将画布坐标转换为屏幕坐标
  const canvasToScreen = useCallback(
    (pos: Position): Position => {
      if (!cy) return pos;
      const pan = cy.pan();
      const zoom = cy.zoom();
      return {
        x: pos.x * zoom + pan.x,
        y: pos.y * zoom + pan.y,
      };
    },
    [cy]
  );

  // 将屏幕坐标（clientX/clientY）转换为画布坐标
  const screenToCanvas = useCallback(
    (clientX: number, clientY: number): Position => {
      if (!cy) return { x: clientX, y: clientY };
      const container = cy.container();
      if (!container) return { x: clientX, y: clientY };
      const rect = container.getBoundingClientRect();
      const pan = cy.pan();
      const zoom = cy.zoom();
      return {
        x: (clientX - rect.left - pan.x) / zoom,
        y: (clientY - rect.top - pan.y) / zoom,
      };
    },
    [cy]
  );

  // 处理控制点鼠标按下
  const handleMouseDown = useCallback(
    (handle: ResizeHandle, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!selectedNode) return;

      setActiveHandle(handle);
      setIsResizing(true);
      startPosRef.current = screenToCanvas(e.clientX, e.clientY);
      // 记录开始时的节点位置和尺寸
      startNodePosRef.current = selectedNode.position();
      startNodeSizeRef.current = {
        width: selectedNode.width(),
        height: selectedNode.height(),
      };

      // 锁定节点，防止拖动
      selectedNode.lock();
    },
    [selectedNode, screenToCanvas]
  );

  // 处理鼠标移动
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (
        !isResizing ||
        !activeHandle ||
        !selectedNode ||
        !startPosRef.current ||
        !startNodePosRef.current ||
        !startNodeSizeRef.current
      )
        return;

      const currentPos = screenToCanvas(e.clientX, e.clientY);
      const deltaX = currentPos.x - startPosRef.current.x;
      const deltaY = currentPos.y - startPosRef.current.y;
      const { minWidth, minHeight } = getMinNodeSize(selectedNode);

      // 基于初始尺寸和位置计算新尺寸
      let newWidth = startNodeSizeRef.current.width;
      let newHeight = startNodeSizeRef.current.height;
      let newX = startNodePosRef.current.x;
      let newY = startNodePosRef.current.y;

      // 根据控制点类型计算新尺寸
      switch (activeHandle) {
        case 'nw': // 左上
          newWidth = Math.max(
            minWidth,
            startNodeSizeRef.current.width - deltaX
          );
          newHeight = Math.max(
            minHeight,
            startNodeSizeRef.current.height - deltaY
          );
          newX =
            startNodePosRef.current.x +
            (startNodeSizeRef.current.width - newWidth) / 2;
          newY =
            startNodePosRef.current.y +
            (startNodeSizeRef.current.height - newHeight) / 2;
          break;
        case 'n': // 上
          newHeight = Math.max(
            minHeight,
            startNodeSizeRef.current.height - deltaY
          );
          newY =
            startNodePosRef.current.y +
            (startNodeSizeRef.current.height - newHeight) / 2;
          break;
        case 'ne': // 右上
          newWidth = Math.max(
            minWidth,
            startNodeSizeRef.current.width + deltaX
          );
          newHeight = Math.max(
            minHeight,
            startNodeSizeRef.current.height - deltaY
          );
          newX =
            startNodePosRef.current.x +
            (newWidth - startNodeSizeRef.current.width) / 2;
          newY =
            startNodePosRef.current.y +
            (startNodeSizeRef.current.height - newHeight) / 2;
          break;
        case 'e': // 右
          newWidth = Math.max(
            minWidth,
            startNodeSizeRef.current.width + deltaX
          );
          newX =
            startNodePosRef.current.x +
            (newWidth - startNodeSizeRef.current.width) / 2;
          break;
        case 'se': // 右下
          newWidth = Math.max(
            minWidth,
            startNodeSizeRef.current.width + deltaX
          );
          newHeight = Math.max(
            minHeight,
            startNodeSizeRef.current.height + deltaY
          );
          newX =
            startNodePosRef.current.x +
            (newWidth - startNodeSizeRef.current.width) / 2;
          newY =
            startNodePosRef.current.y +
            (newHeight - startNodeSizeRef.current.height) / 2;
          break;
        case 's': // 下
          newHeight = Math.max(
            minHeight,
            startNodeSizeRef.current.height + deltaY
          );
          newY =
            startNodePosRef.current.y +
            (newHeight - startNodeSizeRef.current.height) / 2;
          break;
        case 'sw': // 左下
          newWidth = Math.max(
            minWidth,
            startNodeSizeRef.current.width - deltaX
          );
          newHeight = Math.max(
            minHeight,
            startNodeSizeRef.current.height + deltaY
          );
          newX =
            startNodePosRef.current.x +
            (startNodeSizeRef.current.width - newWidth) / 2;
          newY =
            startNodePosRef.current.y +
            (newHeight - startNodeSizeRef.current.height) / 2;
          break;
        case 'w': // 左
          newWidth = Math.max(
            minWidth,
            startNodeSizeRef.current.width - deltaX
          );
          newX =
            startNodePosRef.current.x +
            (startNodeSizeRef.current.width - newWidth) / 2;
          break;
      }

      // 实时更新节点尺寸
      // 关键：对于父节点（compound node），需要使用 min-width/min-height
      // 因为父节点的 width/height 是根据子节点自动计算的
      const isParent = selectedNode.isParent();
      if (isParent) {
        selectedNode.style({
          'min-width': newWidth,
          'min-height': newHeight,
        });
      } else {
        selectedNode.style({
          width: newWidth,
          height: newHeight,
        });
      }
      selectedNode.position({ x: newX, y: newY });

      // 清除缓存
      clearLabelBBoxCache(selectedNode.id());

      // 更新控制点位置
      updateHandlePositions();
    },
    [
      isResizing,
      activeHandle,
      selectedNode,
      screenToCanvas,
      updateHandlePositions,
    ]
  );

  // 处理鼠标松开
  const handleMouseUp = useCallback(() => {
    if (!isResizing || !selectedNode) return;

    // 解锁节点
    selectedNode.unlock();

    // 保存最终尺寸到模型
    const width = selectedNode.width();
    const height = selectedNode.height();
    updateNodeSizeToModel(selectedNode, width, height);

    // 触发回调
    if (onResizeEnd) {
      onResizeEnd(selectedNode, width, height);
    }

    setIsResizing(false);
    setActiveHandle(null);
    startPosRef.current = null;
    startNodePosRef.current = null;
    startNodeSizeRef.current = null;
  }, [isResizing, selectedNode, onResizeEnd]);

  // 绑定全局鼠标事件
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // 如果没有选中节点或没有控制点位置，不渲染
  if (!selectedNode || !handles) {
    return null;
  }

  const halfSize = handleSize / 2;

  return (
    <div
      ref={containerRef}
      className="resize-handles-container"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1000,
      }}
    >
      {(Object.entries(handles) as [ResizeHandle, Position][]).map(
        ([handle, pos]) => {
          const screenPos = canvasToScreen(pos);
          const cursor = getResizeCursor(handle);

          return (
            <div
              key={handle}
              className={`resize-handle resize-handle-${handle}`}
              style={{
                position: 'absolute',
                left: screenPos.x - halfSize,
                top: screenPos.y - halfSize,
                width: handleSize,
                height: handleSize,
                backgroundColor: HANDLE_COLOR,
                border: '1px solid white',
                cursor: cursor,
                pointerEvents: 'auto',
                boxSizing: 'border-box',
              }}
              onMouseDown={e => handleMouseDown(handle, e)}
            />
          );
        }
      )}
    </div>
  );
};

export default ResizeHandles;
